<template>
  <div class="assignment-dashboard-v2">
    <div class="assignment-dashboard-v2__filters">
      <ADFilters
        ref="pmDashboardFilters"
        :staff="staffList"
        :projectsList="projectsList"
        :geos="geosList"
        :keywords="keywordsList"
        :mediaTypes="mediaTypesList"
        :requestTypes="requestTypesList"
        :statuses="statusesList"
        :roles="rolesList"
        @new-assignment="handleNewAssignment"
        @filters-changed="applyFilters"
        @batch-edit="handleBatchEdit"/>
    </div>
    <div class="assignment-dashboard-v2__table">
      <v-skeleton-loader
        v-if="showTableLoader || (isAdminMode ? dropdownsLoading : false)"
        type="table"
        height="100vh"
      ></v-skeleton-loader>
      <ADTableV2
        ref="dashboardTable"
        v-if="
          tableData &&
          !showTableLoader &&
          (isAdminMode ? !dropdownsLoading : true)
        "
        :tableData="tableData"
        :filterShakespeare="filterShakespeare === 0 ? 1 : 0"
        :serverItemsLength="serverItemsLength"
        :isAdminMode="isAdminMode"
        :isTableUpdating="isTableUpdating"
        :statuses="statusOptions"
        :statusesLoading="statusOptionsLoading"
        :showEarnings="earningsMode === 0 ? 1 : 0"
        :filterProject="filterProject"
        :tabledataAmount="tabledataAmount"
        :showWarningModal="showWarningModal"
        @call-edit-form="handleEditForm"
        @update-allowed-statuses="handleAllowedStatuses"
        @refresh-request="refreshTableData"
        @assignment-dashboard-table-refresh="getTableData(true)"
      />
    </div>
    <!-- assignment form -->
    <v-dialog
      v-model="assignmentForm"
      max-width="1720"
      persistent
      @click:outside="closeAssignmentForm"
      @keydown.esc="closeAssignmentForm"
      ref="assignmentFormDialog"
      content-class="assignment-form"
    >
      <ADForm
        v-if="assignmentForm"
        :key="$route.query.openAssignmentForm || 'new-assignment'"
        :itemToEdit="itemToEdit"
        :staffList="staffList"
        @close-assignment-form="handleFormClosing"
        @assignment-saved="handleAssignmentSaved"
        @assignment-dashboard-table-refresh="getTableData(true)"
        @select-assignment="handleHierarchyNavigation"
      />
    </v-dialog>
    <!-- batch edit form -->
    <v-dialog
      v-model="batchEditForm"
      max-width="1200"
      persistent
      scrollable
      content-class="assignment-form"
    >
      <ADBatchEditModal
        v-if="batchEditForm"
        :selectedRows="batchEditRows"
        :statuses="statusOptions"
        :mediaTypes="mediaTypesList"
        :keywords="keywordsList"
        :projects="projectsList"
        :departments="departmentsList"
        :staffList="staffList"
        :bulkQueryEdit="bulkQueryEdit"
        :matchingCount="tabledataAmount"
        :filterQuery="batchUpdateQuery"
        @batch-updated="handleBatchUpdated"
        @close-batch-edit="batchEditForm = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import ADFilters from '@/components/AssignmentDashboard/ADFilters.vue';
import ADTableV2 from '@/components/AssignmentDashboard/ADTableV2.vue';
// vuex
import { mapGetters, mapActions } from "vuex";
// libraries
import _ from "lodash";
// internal
import {
  getAssignments,
  getAssignmentsV2,
  getCustomerPriceList,
  processAssignment,
  getAllStaff,
  getShortProjects,
  getEnum,
  getSpecificEnum,
  getTasks,
  processTask,
} from "@/utils/newDbUtils";
import { customBreakpoint, timeUnits } from "@/utils/mixins";
import { STATUS_COLORS, ROUTES, userRolesList } from "@/utils/constants";
import ADForm from "@/components/AssignmentDashboard/ADForm.vue";
import ADBatchEditModal from "@/components/AssignmentDashboard/ADBatchEditModal.vue";
import SHK from "@/assets/SHK.svg";

export default {
  name: "AssignmentDashboardV2",
  components: {
    ADFilters,
    ADTableV2,
    ADForm,
    ADBatchEditModal
  },
  data() {
    return {
      SHK,
      assignmentForm: false,
      batchEditForm: false,
      batchEditRows: [],
      openConfirmationModal: false,
      bulkStatusValue: "",
      showWarningModal: false,
      showTableLoader: true,
      tableData: null,
      serverItemsLength: 0,
      filterEmail: false,
      filtersDialog: false,
      ppm: null,
      ppy: null,
      filterDeliveryDate: null,
      filterRequestDate: null,
      filterSearch: null,
      expandMenu: false,
      filterProject: null,
      filterShakespeare: null,
      dropdownsLoading: true,
      staffLoading: false,
      staffList: [],
      geosList: [],
      keywordsList: [],
      mediaTypesList: [],
      requestTypesList: [],
      statusesList: [],
      // Department enum entries ({ id, value }) for the batch edit Department
      // dropdown — Settings is the source of truth, same as the other enums.
      departmentsList: [],
      // Role filter options — same source as the PMDashboard Role filter.
      rolesList: userRolesList,
      filterPerson: null,
      roles: ["Primary", "Secondary", "QC"],
      filterRole: null,
      filterGeo: null,
      filterKeyword: null,
      filterMediaType: null,
      filterRequestType: null,
      filterStatus: null,
      isTableUpdating: false,
      statusOptions: [],
      updatedAllowedStatuses: [],
      mediaTypesLoading: true,
      requestTypesLoading: true,
      statusOptionsLoading: true,
      updatedStatusesMode: false,
      earningsMode: null,
      itemsPerPage: 50,
      page: 0,
      sortBy: "schedule_deliveryDate",
      sorting: "desc",
      staffSort: {
        sortByDueTime: false,
        sortByAssignmentTitle: null,
        sortByAssignmentDetail: null,
        sortByWordCount: null,
        sortByFileLocation: null,
        sortByAssignmentStatus: null,
      },
      bulkDeleteConfirm: false,
      bulkDeleteProcessing: false,
      closeModalConfirmation: false,
      notActivePONumber: null,
      closePOConfirmation: false,
      tabledataAmount: 0,
      bulkQueryEditLoading: false,
      countFieldsToUpdate: 0,
      dubModalKey: 0,
      itemToEdit: {},
      presetActive: false,
      presetPendingEntity: false,
      // Set when the form reports a successful save, so closing it triggers one
      // hard table reload instead of the soft refresh a plain close does.
      reloadAfterFormClose: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["user", "isAdmin", "isManager"]),
    ...mapGetters("spreadsheetUpload", ["selectedDashboardRows"]),
    ...mapGetters("assignment", [
      "isEditMode",
      "newAssignmentDatas",
      "newAssignmentModalOpen",
      "dubEditorModalOpen",
      "geoOptions",
      "mediaTypeOptions",
      "requestTypeOptions",
      "projectsList",
      "bulkQueryEdit",
      "updateBatchConfirmation",
      "updatePPCheckModal",
      "updatePPCheckValue",
      "hoursSubmissionModalOpen"
    ]),
    updateBatchConfirmationModal: {
      get() {
        return this.updateBatchConfirmation;
      },
      set() {
        this.setUpdateBatchConfirmation(!this.updateBatchConfirmation);
      },
    },
    updatePPCheckModalStatus: {
      get() {
        return this.updatePPCheckModal;
      },
      set() {
        this.setUpdatePPCheckModal(!this.updatePPCheckModal);
      },
    },
    requestedView() {
      return this.$route.path === "/requested-assignments";
    },
    formattedRequestDate: {
      get() {
        return this.formatPickerDate(this.filterRequestDate);
      },
      set() {
        this.filterRequestDate = null;
      },
    },
    formattedDeliveryDate: {
      get() {
        return this.formatPickerDate(this.filterDeliveryDate);
      },
      set() {
        this.filterDeliveryDate = null;
      },
    },
    bulkQueryEditMode: {
      get() {
        return this.bulkQueryEdit;
      },
      set() {
        this.setBulkQueryEdit(!this.bulkQueryEdit);
      },
    },
    isAdminMode() {
      return !this.filterEmail;
    },
    filterSearchBadge() {
      return this.filterSearch?.length <= 6
        ? this.filterSearch
        : `${this.filterSearch?.slice(0, 6)}...`;
    },
    filtersBadge() {
      return Object.entries(this.$route.query).filter(
        (el) =>
          el[1] !== undefined &&
          (typeof el[1] === "object" && el[1] !== null
            ? el[1].length === 1
            : el[1] !== null)
      ).length;
    },
    query() {
      return {
        page: this.page,
        size: this.itemsPerPage,
        sort:
          this.isAdminMode && this.earningsMode !== 0
            ? `${this.sortBy},${this.sorting}`
            : null,
        sortByDueTime:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.staffSort.sortByDueTime,
        sortByAssignmentTitle:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.staffSort.sortByAssignmentTitle,
        sortByAssignmentDetail:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.staffSort.sortByAssignmentDetail,
        sortByWordCount:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.staffSort.sortByWordCount,
        sortByFileLocation:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.staffSort.sortByFileLocation,
        sortByAssignmentStatus:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.staffSort.sortByAssignmentStatus,
        search: this.filterSearch,
        requestDate: this.filterRequestDate,
        deliveryDate:
          this.isAdminMode && this.earningsMode !== 0
            ? this.filterDeliveryDate
            : null,
        dueDate:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.filterDeliveryDate,
        ppy: this.ppy,
        ppm: this.ppm?.toUpperCase(),
        geo: this.filterGeo,
        keyword: this.filterKeyword,
        mediaType: this.filterMediaType,
        requestType: this.filterRequestType,
        projectId: this.filterProject?.id,
        personId:
          this.isAdminMode && this.earningsMode !== 0
            ? Array.isArray(this.filterPerson)
              ? this.filterPerson.map((p) => p.user.id).join(",") || null
              : this.filterPerson?.user?.id
            : null,
        personRole:
          this.filterRole === "Creator"
            ? "createdBy"
            : this.filterRole?.toLowerCase(),
        fileLocation: this.filterShakespeare === 0 ? "Shakespeare" : null,
        assignmentStatus: this.requestedView ? "Requested" : this.filterStatus,
      };
    },
    // Filter-only view of `query` for the query-scoped batch update ("All matching").
    // Pagination and sorting must not narrow which records get updated.
    batchUpdateQuery() {
      return _.omit(this.query, [
        "page",
        "size",
        "sort",
        "sortByDueTime",
        "sortByAssignmentTitle",
        "sortByAssignmentDetail",
        "sortByWordCount",
        "sortByFileLocation",
        "sortByAssignmentStatus",
      ]);
    },
    totalEarnings() {
      return this.tableData
        ?.reduce(
          (a, b) =>
            a +
            (this.isActiveUserEmail(b.primary?.email)
              ? b.pricing.primaryTotal
              : this.isActiveUserEmail(b.secondary?.email)
              ? b.pricing.secondaryTotal
              : 0),
          0
        )
        ?.toFixed(2);
    },
  },
  mixins: [customBreakpoint, timeUnits],
  watch: {
    '$route.query.openAssignmentForm': {
      immediate: true,
      async handler(id) {
        if (!id) return;
        this.assignmentForm = true;
      }
    },
    // Lock the page behind the modal so the only scrollbars on screen are the
    // ones inside the form's own columns (same as the PM Dashboard task form).
    assignmentForm(val) {
      document.body.style.overflow = val ? 'hidden' : '';
      document.documentElement.style.overflow = val ? 'hidden' : '';
    },
    updateBatchConfirmationModal(newValue) {
      if (newValue) this.countFieldsToUpdate = this.countPTags();
    },
    filterPerson(newValue) {
      if (this.isAdminMode && newValue === null) this.filterRole = "";
    },
    filterSearch(newValue) {
      const currentQuery = this.$route.query;
      const newQuery =
        newValue !== ""
          ? {
              ...currentQuery,
              Search: newValue,
            }
          : _.omit(currentQuery, ["Search"]);

    },
    requestedView() {
      this.clearFilter();
    },
    'newAssignmentDatas.0': {
      handler(newValue, oldValue) {
        if (newValue?.pricing?.purchaseOrder?.purchaseOrderNumber !== oldValue?.pricing?.purchaseOrder?.purchaseOrderNumber
          && !newValue?.pricing?.purchaseOrder?.isActive
          && oldValue?.pricing?.purchaseOrder
          && newValue?.pricing?.purchaseOrder) {
            this.notActivePONumber = newValue?.pricing?.purchaseOrder?.purchaseOrderNumber;
            this.closePOConfirmation = true;
          }
      },
      immediate: false,
    },
    newAssignmentModalOpen() {
      if (!this.newAssignmentModalOpen) {
        this.newAssignmentDatas[0].dubEditorAssignment =
        this.newAssignmentDatas[0].project
        = null;
      }
    }
  },
  beforeDestroy() {
    // Never leave the page locked if the view is torn down with the form open.
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  },
  mounted() {
    this.handleQuery();
    this.getStatuses();
    this.getMediaTypes();
    this.getRequestTypes();
    this.handleDropdowns();
    this.getKeywords();
    this.getPriceList();
    this.getGeos();
    this.getDepartments();
  },
  methods: {
    ...mapActions("spreadsheetUpload", ["setSelectedDashboardRows"]),
    ...mapActions("assignment", [
      "setNewAssignmentModalOpen",
      "setDubEditorModalOpen",
      "setCommonData",
      "setBulkEditing",
      "setBulkQueryEdit",
      "setUpdateBatchConfirmation",
      "setUpdatePPCheckModal",
      "setHoursSubmissionModalOpen",
      "setJumpOverPPCheck",
      "setUpdatePPCheckValue",
      "setIsEditing"
    ]),
    ...mapActions("flashMessage", ["handleFlash"]),
    cancelAssignmentUpdate() {
      this.setUpdatePPCheckModal(false);
      this.setUpdatePPCheckValue(null);
    },
    updateAssignment() {
      this.setJumpOverPPCheck(false);
      this.setUpdatePPCheckModal(false);
      this.setUpdatePPCheckValue(null);
      this.$refs.newAssignmentModal.handleAssignmentsProcessing();
    },
    removeAssignment() {
      this.setJumpOverPPCheck(false);
      this.setUpdatePPCheckModal(false);
      this.setUpdatePPCheckValue(null);
      this.$refs.newAssignmentModal.handleAssignmentsDeletion();
    },
    countPTags() {
      const modal = this.$refs.newAssignmentModal.$el;
      const pTags = modal.querySelectorAll('p');
      const filteredPTags = Array.from(pTags).filter(p => p.textContent.trim() === 'Value was changed');
      return filteredPTags.length;
    },
    exportPlainText() {
      const checkedData = this.tableData.filter((item) => item.checked && item.dubEditorAssignment);
      
      const stripHTML = (html) => {
        const tags = ["<u>", "</u>", "<p>", "</p>"];
        const updatedHtml = html
            .replace(/<strong>/gi, "<b>")
            .replace(/<\/strong>/gi, "</b>")
            .replace(/<em>/gi, "<i>")
            .replace(/<\/em>/gi, "</i>");
        const regex = new RegExp(tags.join("|"), "gi");
        return updatedHtml.replace(regex, "");
      };

      const content = checkedData
        .map((el) => {
          let fragmentsToExport = null;
          if (
            el.dubEditorAssignment && 
            el.dubEditorAssignment.qcFragments &&
            el.dubEditorAssignment.qcFragments.complete
          ) {
            fragmentsToExport = el.dubEditorAssignment.qcFragments.fragments;
          } else if (
            el.dubEditorAssignment && 
            el.dubEditorAssignment.secondaryFragments &&
            el.dubEditorAssignment.secondaryFragments.complete
          ) {
            fragmentsToExport = el.dubEditorAssignment.secondaryFragments.fragments;
          } else if (
            el.dubEditorAssignment && 
            el.dubEditorAssignment.primaryFragments
          ) {
            fragmentsToExport = el.dubEditorAssignment.primaryFragments.fragments;
          }
          
          if (fragmentsToExport) {
            fragmentsToExport.sort((a, b) => a.order - b.order);
            return fragmentsToExport
              .map((item) => `${item.fragmentName}\n${stripHTML(item.text)}`)
              .join("\n\n");
          }
          return '';
        })
        .filter(content => content.trim() !== '')
        .join("\n\n-------\n\n");

      const blob = new Blob([content], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "multi.txt";
      link.click();
      URL.revokeObjectURL(link.href);
    },
    async exportAsExcel() {
      const checkedData = this.tableData.filter((item) => item.checked && item.dubEditorAssignment);
      const stripHTML = (html) => {
        const tags = ["<u>", "</u>", "<p>", "</p>"];
        const updatedHtml = html
          .replace(/<strong>/gi, "<b>")
          .replace(/<\/strong>/gi, "</b>")
          .replace(/<em>/gi, "<i>")
          .replace(/<\/em>/gi, "</i>");
        const regex = new RegExp(tags.join("|"), "gi");
        return updatedHtml.replace(regex, "");
      };
      const arraysList = checkedData.map((el) => {
        let fragmentsToExport = null;

        if (
          el.dubEditorAssignment &&
          el.dubEditorAssignment.qcFragments &&
          el.dubEditorAssignment.qcFragments.complete
        ) {
          fragmentsToExport = el.dubEditorAssignment.qcFragments.fragments;
        } else if (
          el.dubEditorAssignment &&
          el.dubEditorAssignment.secondaryFragments &&
          el.dubEditorAssignment.secondaryFragments.complete
        ) {
          fragmentsToExport = el.dubEditorAssignment.secondaryFragments.fragments;
        } else if (
          el.dubEditorAssignment &&
          el.dubEditorAssignment.primaryFragments
        ) {
          fragmentsToExport = el.dubEditorAssignment.primaryFragments.fragments;
        }

        if (fragmentsToExport) {
          fragmentsToExport.sort((a, b) => a.order - b.order);
        }
        return fragmentsToExport || [];
      });

      const ExcelJS = require('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Exported Data");
      const headers = [];
      arraysList.forEach((array) => {
        array.forEach((item) => {
          if (!headers.includes(item.fragmentName)) {
            headers.push(item.fragmentName);
          }
        });
      });
      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFC9F2EE" },
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      headers.forEach((_, index) => {
        worksheet.getColumn(index + 1).width = 50;
      });
      arraysList.forEach((array) => {
        const row = headers.map(() => "");
        array.forEach((item) => {
          const columnIndex = headers.indexOf(item.fragmentName);
          row[columnIndex] = stripHTML(item.text);
        });
        worksheet.addRow(row);
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "exported_data.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    },
    updateBatchAssignments() {
      this.bulkQueryEditLoading = true;
      this.$refs.newAssignmentModal.handleAssignmentsUpdate();
      this.bulkQueryEditLoading = false;
      this.setUpdateBatchConfirmation(false);
    },
    fullName(item) {
      return `${item.user.firstName} ${item.user.lastName}`;
    },
    applySearch(reset) {
      if (reset) this.filterSearch = null;
      this.handleFilterQuery("Search", this.filterSearch);
    },
    handleDueValue(dueTime) {
      return dueTime
        ? new Date(
            `${dueTime[1]}/${dueTime[2]}/${dueTime[0]} ${this.$moment(
              dueTime[3],
              "hh:mm"
            ).format("hh:mm A")}`
          )
        : null;
    },
    getTableData(hardReload) {
      if (hardReload) this.showTableLoader = true;
      else this.isTableUpdating = true;
      /* getAssignmentsV2({
        criteria: [
          {
            filterKey: "ASSIGNMENT_STATUS",
            operation: "EQUALS",
            value: "ACTIVE"
          }
        ],
        logicalOperator: "AND",
        page: 0,
        size: 20,
        sortBy: "deliveryDate",
        sortDirection: "DESC"
      }) */
      getAssignments({
        query: this.query,
        staffId:
          this.isAdminMode && this.earningsMode !== 0
            ? null
            : this.user.user.id,
      }).then((resp) => {
        if (resp?.status > 204) {
          this.tableData = [];
          this.handleFlash({ response: resp, show: true });
        } else {
          this.tabledataAmount = resp.totalElements;
          this.tableData = resp.content.map((el, i) => ({
            ...el,
            dueTime:
              this.isAdminMode && this.earningsMode !== 0
                ? null
                : this.handleDueValue(el.schedule.dueTime),
            dueGroup:
              this.isAdminMode && this.earningsMode !== 0
                ? null
                : this.$moment(this.handleDueValue(el.schedule.dueTime)).format(
                    "YYYY-MM-DD"
                  ),
            checked: false,
            index: i,
          }));
        }
        this.serverItemsLength = resp?.totalElements ?? 0;
        this.showTableLoader = this.isTableUpdating = false;
      });
    },
    refreshTableData(options) {
      const sortKey = options.sortBy[0];
      const sortDir = options.sortDesc[0];
      const handleStaffSort = (key) => {
        const capitalizeKey = () => {
          const extractedKey =
            key === "dueTime" ? key : key.split("[")[1].slice(0, -1);
          return extractedKey.charAt(0).toUpperCase() + extractedKey.slice(1);
        };
        const handleDir = () => {
          if (sortDir === undefined || sortDir) return key !== "dueTime";
          else return key === "dueTime";
        };
        Object.keys(this.staffSort).forEach((el) => {
          this.staffSort[el] =
            el === `sortBy${capitalizeKey()}` ? handleDir() : null;
        });
      };
      const handleSortKey = () => {
        return sortKey === undefined
          ? "schedule_deliveryDate"
          : `${sortKey.split("[")[0]}_${sortKey.split("[")[1].slice(0, -1)}`;
      };
      this.itemsPerPage =
        options.itemsPerPage === -1
          ? this.serverItemsLength
          : options.itemsPerPage;
      this.page = options.page - 1;
      if (this.isAdminMode && this.earningsMode !== 0) {
        this.sortBy = handleSortKey();
        this.sorting = sortDir === undefined || sortDir ? "desc" : "asc";
      } else handleStaffSort(sortKey ?? "dueTime");
      this.getTableData();
    },
    handleFilterQuery(param, data) {
      const query = {
        ...this.$route.query,
        [param]:
          param === "Person" && data
            ? `${data.user.firstName} ${data.user.lastName}`
            : param === "Project" && data
            ? data?.name
            : data,
      };

      this[param === "ppy" || param === "ppm" ? param : `filter${param}`] =
        data;

      if (param === "ppm" && !this.ppy) return;

      if (this.$refs.dashboardTable?.options?.page === 1) this.getTableData();
      else this.$refs.dashboardTable.options.page = 1;
    },
    clearFilter(date) {
      let query = this.$route.query;

      if (date) {
        query = _.omit(query, [date]);
        this[`filter${date}`] = null;
      } else {
        this.filterShakespeare =
          this.filterProject =
          this.filterRole =
          this.filterStatus =
          this.filterGeo =
          this.filterKeyword =
          this.filterMediaType =
          this.filterRequestType =
          this.filterSearch =
          this.ppm =
          this.ppy =
            null;
        this.filterPerson =
          this.isAdminMode && this.earningsMode === 0
            ? this.filterPerson
            : null;
        query = {};
      }

      if (!date) this.filterRequestDate = this.filterDeliveryDate = null;

      if (this.$refs.dashboardTable?.options?.page === 1) this.getTableData();
      else this.$refs.dashboardTable.options.page = 1;
    },
    handleBulkStatusSelect(status) {
      this.isTableUpdating = true;
      for (let item of this.selectedDashboardRows) {
        if (
          item.project.id === 115 &&
          item.assignmentDetails.requestType === "Adaptation" &&
          status === "Delivered" &&
          (!item.assignmentDetails.wordCount || !item.pricing.workTypes[0].quantity)
        ) {
          this.showWarningModal = true;
          this.isTableUpdating = false;
          return;
        }
      }
      const bulkStatusUpdate = this.selectedDashboardRows.map((el) =>
        processAssignment({
          data: {
            assignmentStatus: status,
          },
          id: el.id,
          patch: true,
        })
      );
      Promise.all(bulkStatusUpdate)
        .then((resp) => {
          const failedUpdates = resp.filter((el) => el.status > 204);
          if (failedUpdates.length) {
            const errMsg = failedUpdates
              .map(
                (el) => `${JSON.parse(el.config.id)} / Error: ${el.data.error}`
              )
              .join(".\n");
            this.handleFlash({
              response: {
                data: {
                  statusCode: 500,
                  error: `Failed to update statuses of assignments with IDs:\n${errMsg}`,
                },
              },
              show: true,
            });
          } else {
            // handle Task status on bulk Assignment status change
            if (status.endsWith("Complete")) {
              const cueIDs = [
                ...new Set(
                  this.selectedDashboardRows.map((el) => el.cueTaskId)
                ),
              ];
              cueIDs.forEach((cueID) => {
                getAssignments({ query: { search: cueID } }).then(
                  (assgnResp) => {
                    const assgnsWithCueIdField = assgnResp.content.filter(
                      (el) => el.cueTaskId === cueID
                    );
                    if (
                      assgnsWithCueIdField.every(
                        (el) => el.assignmentDetails.assignmentStatus === status
                      )
                    ) {
                      getTasks({ query: { search: cueID } }).then(
                        (taskResp) => {
                          const task = taskResp.content.find(
                            (el) => el.cueTaskId === cueID
                          );
                          if (task)
                            processTask({
                              data: {
                                taskStatus: status,
                              },
                              id: task.id,
                              patch: true,
                            }).then((resp) =>
                              this.handleFlash({ response: resp, show: true })
                            );
                        }
                      );
                    }
                  }
                );
              });
            }
            this.handleFlash({ response: { status: 200 }, show: true });
          }
          this.isTableUpdating = false;
          this.bulkStatusValue = "";
          this.getTableData(true);
          this.setSelectedDashboardRows([]);
        })
        .catch((err) => {
          console.error(err);
          this.handleFlash({ response: err, show: true });
          this.isTableUpdating = false;
        });
    },
    getStatuses() {
      getEnum("AssignmentStatus").then((data) => {
        this.statusOptions = data.map((el) => ({
          value: el,
          color: STATUS_COLORS.find((color) => color.statuses.includes(el))
            ?.value,
        }));
        // Raw status strings for the ADFilters Status dropdown (same source the
        // legacy dashboard's status filter uses).
        this.statusesList = data;
        this.setCommonData({ field: "statusOptions", data: data });
        this.statusOptionsLoading = false;
      });
    },
    // Shared transform for the Settings-sourced enum filter lists: keep ACTIVE
    // entries in display order and expose the field the assignment stores (and
    // the backend filters on), so the dropdown label doubles as the filter value.
    // GEO / Request Type are keyed enums (Settings shows a Key column) → use
    // `key`; Media Type / Keyword have no Key column → the assignment stores the
    // `value`, so use that.
    mapEnumFilterList(resp, field = "key") {
      return (resp?.data || [])
        .filter((el) => el.status === "ACTIVE" && el[field])
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map((el) => el[field]);
    },
    getKeywords() {
      // Options for the assignment form dropdowns (unchanged source).
      getEnum("Keyword").then((data) => {
        this.setCommonData({ field: "keywordOptions", data });
        this.mediaTypesLoading = false;
      });
      // Filter dropdown list — same Settings enum source as GEO. Keyword has no
      // Key column in Settings, so the assignment stores the `value`.
      getSpecificEnum("keywords").then((resp) => {
        this.keywordsList = this.mapEnumFilterList(resp, "value");
      });
    },
    getMediaTypes() {
      // Options for the assignment form dropdowns (unchanged source).
      getEnum("MediaType").then((data) => {
        this.setCommonData({ field: "mediaTypeOptions", data });
        this.mediaTypesLoading = false;
      });
      // Filter dropdown list — same Settings enum source as GEO. Media Type has
      // no Key column in Settings, so the assignment stores the `value`.
      getSpecificEnum("media-types").then((resp) => {
        this.mediaTypesList = this.mapEnumFilterList(resp, "value");
      });
    },
    getRequestTypes() {
      // Options for the assignment form dropdowns (unchanged source).
      getEnum("RequestType").then((data) => {
        this.setCommonData({ field: "requestTypeOptions", data });
        this.requestTypesLoading = false;
      });
      // Filter dropdown list — same Settings enum source as GEO. Request Type is
      // a keyed enum (Settings shows a Key column), so use the `key`.
      getSpecificEnum("request-types").then((resp) => {
        this.requestTypesList = this.mapEnumFilterList(resp);
      });
    },
    getGeos() {
      // Source the GEO filter list from the same enum the Settings page shows
      // (getSpecificEnum("geos")), keeping only active entries in display order.
      getSpecificEnum("geos").then((resp) => {
        this.geosList = this.mapEnumFilterList(resp);
      });
    },
    getDepartments() {
      // Batch edit's Department dropdown needs the whole enum entry (it publishes
      // the `id`), so keep the objects rather than mapping down to labels.
      getSpecificEnum("departments").then((resp) => {
        const list = (resp?.data || [])
          .filter((el) => el.status === "ACTIVE")
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        this.departmentsList = list;
        this.setCommonData({ field: "departmentOptions", data: list });
      });
    },
    getPriceList() {
      console.log("go");
      getCustomerPriceList().then((data) => {
        console.log("data", data);
        this.setCommonData({ field: "workTypeReferences", data });
        this.requestTypesLoading = false;
      });
    },
    handleRoles() {
      return new Promise((resolve, reject) => {
        resolve(this.roles.push("Creator"));
      });
    },
    handleStaff() {
      this.staffLoading = true;
      getAllStaff().then((data) => {
        this.staffList = data;
        const driList = data.filter((el) => el.user.userRole === "ADMIN");
        this.setCommonData({
          field: "driList",
          data: driList,
        });
        this.staffLoading = false;
      });
    },
    async handleDropdowns() {
      const resp = await getShortProjects();
      this.setCommonData({ field: "projectsList", data: resp.data });

      if (this.isAdminMode) {
        let dropdowns = [getEnum("GEO"), this.handleRoles()];

        const presetFilters =
          this.$refs.pmDashboardFilters?.activeFiltersList || [];
        const presetHasPerson =
          this.presetActive &&
          presetFilters.some(
            (f) => f.title === "Person" && f.value && f.value.length
          );

        if (this.$route.query?.Person || presetHasPerson)
          dropdowns.push(getAllStaff());

        Promise.all(dropdowns)
          .then((resp) => {
            this.setCommonData({ field: "geoOptions", data: resp[0] });

            if (this.presetActive && this.presetPendingEntity) {
              // Resolve the deferred preset Project / Person now that the
              // dropdown data is available, then run the first fetch.
              const projectFilter = presetFilters.find(
                (f) => f.title === "Project" && f.value
              );
              if (projectFilter) {
                this.filterProject =
                  (this.projectsList || []).find(
                    (el) => el.projectName === projectFilter.value
                  ) || null;
              }
              const personFilter = presetFilters.find(
                (f) => f.title === "Person" && f.value && f.value.length
              );
              if (personFilter) {
                this.staffList = resp[resp.length - 1];
                const personNames = Array.isArray(personFilter.value)
                  ? personFilter.value
                  : [personFilter.value];
                this.filterPerson = this.staffList.filter((el) =>
                  personNames.includes(`${el.user.firstName} ${el.user.lastName}`)
                );
              }
              this.handleUser();
            } else if (
              this.$route.query?.Project ||
              this.$route.query?.Person
            ) {
              if (this.$route.query?.Project) {
                this.filterProject = this.projectsList.find(
                  (el) => this.$route.query.Project === el.projectName
                );
              }
              if (this.$route.query?.Person) {
                this.staffList = resp[resp.length - 1];
                this.filterPerson = this.staffList.find(
                  (el) =>
                    this.$route.query.Person.includes(el.user.firstName) &&
                    this.$route.query.Person.includes(el.user.lastName)
                );
              }

              this.handleUser();
            }
            if (this.$route.query?.openAssignmentForm) {
              this.handleForm({ id: this.$route.query.openAssignmentForm });
            }
            this.dropdownsLoading = false;
          })
          .catch((err) => {
            console.error(err);
            this.dropdownsLoading = false;
          });
      } else {
        this.dropdownsLoading = false;
      }
    },
    handleQuery() {
      // A saved filters preset (restored by ADFilters on mount) takes precedence
      // over the default pay-period filters on page load / reload.
      const filtersComp = this.$refs.pmDashboardFilters;
      const presetFilters = filtersComp?.activeFiltersList || [];
      const presetSearch = filtersComp?.searchValue || "";

      if ((presetFilters.length || presetSearch) && !this.requestedView) {
        this.presetActive = true;
        this.mapFiltersToProps(presetFilters, presetSearch);
        const isAdminUser = this.isAdmin || this.isManager;
        const hasPerson = presetFilters.some(
          (f) => f.title === "Person" && f.value && f.value.length
        );
        const hasProject = presetFilters.some(
          (f) => f.title === "Project" && f.value
        );
        // Project / Person need the dropdown data to resolve into objects, so
        // defer that first fetch to handleDropdowns (mirrors the route-query flow).
        // Only admins/managers have that deferred resolution path — for everyone
        // else fetch right away so the table always loads.
        this.presetPendingEntity = (hasPerson || hasProject) && isAdminUser;
        if (!hasPerson && isAdminUser) this.handleStaff();
        if (this.presetPendingEntity) this.filterEmail = false;
        else this.handleUser();
        return;
      }

      let query = { ...this.$route.query };

      if (_.isEmpty(query) && !this.requestedView) {
        this.ppm = this.$moment().format("MMMM");
        this.ppy = this.$moment().format("YYYY");
        query = {
          ppm: this.ppm,
          ppy: this.ppy,
        };
      } else {
        Object.keys(query).forEach((param) => {
          this[param === "ppy" || param === "ppm" ? param : `filter${param}`] =
            param === "Shakespeare" ? Number(query[param]) : query[param];
        });
      }

      if (!query?.Person) {
        if (!query?.Project) this.handleUser();
        if (this.isAdminMode) this.handleStaff();
      }
    },
    applyFilters(payload) {
      // Runtime filter changes coming from ADFilters — remap and refetch.
      const { activeFilters = [], search = "" } = payload || {};
      this.mapFiltersToProps(activeFilters, search);

      if (this.$refs.dashboardTable?.options?.page === 1) this.getTableData();
      else if (this.$refs.dashboardTable)
        this.$refs.dashboardTable.options.page = 1;
      else this.getTableData();
    },
    mapFiltersToProps(activeFilters, search) {
      // Translate the ADFilters active-filter list into the filter props that
      // the `query` computed (and getTableData) rely on.
      const byTitle = {};
      (activeFilters || []).forEach((filter) => {
        byTitle[filter.title] = filter.value;
      });

      const payPeriod = byTitle["Pay Period"];
      this.ppm = payPeriod ? this.$moment(payPeriod).format("MMMM") : null;
      this.ppy = payPeriod ? this.$moment(payPeriod).format("YYYY") : null;
      this.filterRequestDate = byTitle["Request Date"] || null;
      this.filterDeliveryDate = byTitle["Delivery Date"] || null;
      this.filterProject = byTitle["Project"]
        ? (this.projectsList || []).find((el) => el.projectName === byTitle["Project"]) ||
          null
        : null;
      this.filterGeo = byTitle["GEO"] || null;
      // Person is multi-select: byTitle["Person"] is an array of full names.
      // Resolve them into staff objects (null when nothing is selected).
      const personNames = Array.isArray(byTitle["Person"])
        ? byTitle["Person"]
        : byTitle["Person"]
        ? [byTitle["Person"]]
        : [];
      this.filterPerson = personNames.length
        ? (this.staffList || []).filter((el) =>
            personNames.includes(`${el.user.firstName} ${el.user.lastName}`)
          )
        : null;
      this.filterRole = byTitle["Role"] || null;
      this.filterStatus = byTitle["Status"] || null;
      this.filterKeyword = byTitle["Keyword"] || null;
      this.filterMediaType = byTitle["Media Type"] || null;
      this.filterRequestType = byTitle["Request Type"] || null;
      this.filterSearch = search || null;
    },
    handleUser() {
      this.filterEmail =
        this.isAdmin || this.isManager ? false : this.user.user.email;
      this.getTableData(true);
    },
    handleAllowedStatuses(statuses) {
      this.updatedAllowedStatuses = [...statuses];
      this.updatedStatusesMode = statuses.length;
    },
    closeAssignmentForm() {
      this.openConfirmationModal = !this.openConfirmationModal;
    },
    // "New assignment" — open the form on a blank draft. The route must not carry
    // openAssignmentForm (that is what makes ADForm fetch an existing record),
    // and the shared edit flag has to be cleared so a previously edited
    // assignment does not leave the store in edit mode.
    handleNewAssignment() {
      this.setIsEditing(false);
      this.itemToEdit = {};
      if (this.$route.query.openAssignmentForm) {
        this.$router
          .push({
            path: `/${ROUTES.assignmentDashboardV2}`,
            query: _.omit(this.$route.query, ["openAssignmentForm"]),
          })
          .catch(() => {});
      }
      this.assignmentForm = true;
    },
    // The form saved and closes itself immediately after this. Flag the close so
    // it does one hard reload instead of the soft refresh a plain close does —
    // otherwise the row is fetched twice and the two loaders race.
    handleAssignmentSaved() {
      this.reloadAfterFormClose = true;
    },
    handleEditForm(tableRow) {
      this.setIsEditing(!!tableRow.id);
      const query = {
        ...this.$route.query,
        openAssignmentForm: tableRow.id
      };
      this.$router
        .push({
          path: `/${ROUTES.assignmentDashboardV2}`,
          query
        })
        .catch(() => {});
    },
    // A sibling assignment was picked in the form's Task/Assignment hierarchy
    // panel. Swapping the id in the query is enough — the `:key` on ADForm makes
    // it remount and refetch, since the form only loads in its mounted hook.
    handleHierarchyNavigation(assignmentId) {
      if (!assignmentId) return;
      this.setIsEditing(true);
      const query = {
        ...this.$route.query,
        openAssignmentForm: assignmentId,
      };
      this.$router
        .push({ path: `/${ROUTES.assignmentDashboardV2}`, query })
        .catch(() => {});
    },
    handleBatchEdit(selectedRows) {
      // Triggered from the ADFilters additional menu ("Batch edit selected"),
      // which only appears once at least one table row is checked. Snapshot the
      // current selection so it survives the selection being cleared on publish.
      const rows =
        selectedRows && selectedRows.length
          ? selectedRows
          : this.selectedDashboardRows;
      this.batchEditRows = [...rows];
      this.batchEditForm = true;
    },
    handleBatchUpdated() {
      // Applied to all selected assignments — refresh the table, drop the
      // selection (including any "All matching" scope), and close the modal.
      this.batchEditForm = false;
      this.setSelectedDashboardRows([]);
      this.setBulkQueryEdit(false);
      this.getTableData(true);
    },
    handleFormClosing() {
      let query = { ...this.$route.query };
      query && query.openAssignmentForm && delete query.openAssignmentForm;
      this.$router
        .push({ path: `/${ROUTES.assignmentDashboardV2}`, query })
        .catch(() => {});
      this.getTableData(this.reloadAfterFormClose);
      this.reloadAfterFormClose = false;
      this.setIsEditing(false);
      this.assignmentForm = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.assignment-form {
  border-radius: 15px !important;
  min-width: 600px !important;
}
.assignment-dashboard-v2 {
  max-width: 1500px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;

  &__table {
    margin-top: 20px;
    box-shadow: 0px 0px 10px 0px #0000001A;
    border-radius: 10px;
  }
}

.icon {
  font-size: 16px;
  margin-right: 15px;
  padding: 0px 8px;
  display: flex;
  align-items: center;
}

.text {
  white-space: nowrap;
  padding-right: 15px;
  margin-bottom: 4px;
}

.vertical-middle {
  vertical-align: middle;
}

::v-deep {
  .selector.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
    > .v-input__control
    > .v-input__slot {
    padding-right: 0;
  }
  .selector.v-text-field.v-text-field--solo .v-input__append-inner {
    padding-left: 0;
  }
  .selector.v-autocomplete.v-select.v-input--is-focused input {
    min-width: 0;
  }
  .selector.v-text-field.v-input--dense:not(.v-text-field--outlined) input {
    padding: 2px 0;
  }
  .filters {
    position: absolute;
    top: 20px;
  }
}

@import "~vuetify/src/styles/settings/_variables";

@media #{map-get($display-breakpoints, 'lg-and-down')} {
  .v-application .overline {
    font-size: 0.6rem !important;
  }
}
</style>